/**
 * SDR Stock Manager - Supabase Storage Bucket & Image Garbage Collection
 * @module services/storage-bucket
 */

import { SUPABASE_CONFIG } from '../config/constants.js';
import { getState } from '../state/store.js';
import { getSupabaseConfig, getSupabaseClient } from './supabase.js';
import { getAuthAccessToken } from './auth.js';
import { generateId } from '../utils/search.js';

/**
 * Resolves the active Supabase Storage bucket name.
 * @returns {string}
 */
export function getSupabaseStorageBucket() {
  const state = getState();
  const bucket = String(state.settings?.r2UploadUrl || '').trim();
  if (!bucket || /^https?:\/\//i.test(bucket)) {
    return SUPABASE_CONFIG.STORAGE_BUCKET;
  }
  return bucket;
}

/**
 * Uploads a Base64 data URL to the Supabase Storage bucket and returns its public HTTPS URL.
 * @param {string} dataUrl - Base64 image data URL
 * @param {string} [itemId] - Item lot identifier used for naming
 * @returns {Promise<string>} Public HTTPS image URL or original dataUrl if upload skipped
 */
export async function uploadDataUrlToSupabaseStorage(dataUrl, itemId = '') {
  const bucket = getSupabaseStorageBucket();
  const { url, anonKey } = getSupabaseConfig();

  if (!bucket || !url || !anonKey || !dataUrl || !dataUrl.startsWith('data:image/')) {
    return dataUrl || '';
  }

  const match = dataUrl.match(/^data:(image\/[a-zA-Z0-9.+-]+);base64,(.+)$/);
  if (!match) return dataUrl;

  const contentType = match[1];
  const extension = (contentType.split('/')[1] || 'jpg').replace('jpeg', 'jpg');
  const safeId = String(itemId || generateId('image')).replace(/[^a-zA-Z0-9_-]/g, '-');
  const objectPath = `inventory/${new Date().toISOString().slice(0, 10)}/${safeId}-${Date.now()}.${extension}`;

  // Decode Base64 to Uint8Array binary buffer
  const binaryString = atob(match[2]);
  const bytes = new Uint8Array(binaryString.length);
  for (let i = 0; i < binaryString.length; i++) {
    bytes[i] = binaryString.charCodeAt(i);
  }

  const client = getSupabaseClient();
  if (client && client.storage && typeof client.storage.from === 'function') {
    const bucketClient = client.storage.from(bucket);
    const { error: uploadError } = await bucketClient.upload(objectPath, bytes, {
      contentType,
      upsert: false
    });
    if (uploadError) {
      throw new Error(uploadError.message || 'Storage upload failed');
    }
    const { data: urlData } = bucketClient.getPublicUrl(objectPath);
    return urlData?.publicUrl || `${url}/storage/v1/object/public/${encodeURIComponent(bucket)}/${objectPath}`;
  }

  // Direct fetch fallback
  const response = await fetch(`${url}/storage/v1/object/${encodeURIComponent(bucket)}/${objectPath}`, {
    method: 'POST',
    headers: {
      apikey: anonKey,
      Authorization: `Bearer ${getAuthAccessToken(anonKey)}`,
      'Content-Type': contentType,
      'x-upsert': 'false'
    },
    body: bytes
  });

  if (!response.ok) {
    const detail = await response.text().catch(() => '');
    throw new Error(detail || `Supabase Storage upload failed (${response.status})`);
  }

  return `${url}/storage/v1/object/public/${encodeURIComponent(bucket)}/${objectPath}`;
}

/**
 * Extracts the relative bucket object path from a Supabase Storage public URL.
 * @param {string} fileUrl
 * @returns {string} Object path or empty string
 */
export function getSupabaseStorageObjectPath(fileUrl) {
  const bucket = getSupabaseStorageBucket();
  const { url } = getSupabaseConfig();
  if (!bucket || !url || !fileUrl || fileUrl.startsWith('data:')) return '';

  try {
    const storageUrl = new URL(fileUrl);
    const projectUrl = new URL(url);
    if (storageUrl.origin !== projectUrl.origin) return '';

    const publicPrefix = `/storage/v1/object/public/${encodeURIComponent(bucket)}/`;
    const objectPrefix = `/storage/v1/object/${encodeURIComponent(bucket)}/`;
    let encodedPath = '';

    if (storageUrl.pathname.startsWith(publicPrefix)) {
      encodedPath = storageUrl.pathname.slice(publicPrefix.length);
    } else if (storageUrl.pathname.startsWith(objectPrefix)) {
      encodedPath = storageUrl.pathname.slice(objectPrefix.length);
    }

    if (!encodedPath) return '';
    return encodedPath
      .split('/')
      .map(part => decodeURIComponent(part))
      .join('/');
  } catch (err) {
    console.warn('Unable to parse Supabase Storage URL:', err);
    return '';
  }
}

/**
 * Checks if an image URL is referenced by another active (non-deleted) inventory lot.
 * @param {string} imageUrl
 * @param {string} currentItemId
 * @param {import('../types/index.js').InventoryItem[]} [inventoryOverride]
 * @returns {boolean}
 */
export function isImageUsedByAnotherActiveItem(imageUrl, currentItemId, inventoryOverride = null) {
  if (!imageUrl) return false;
  const inventory = inventoryOverride || getState().inventory || [];
  return inventory.some(
    item => item.id !== currentItemId && !item.deleted && item.image === imageUrl
  );
}

/**
 * Deletes an image object from Supabase Storage bucket if no other active items reference it.
 * @param {string} imageUrl
 * @param {string} currentItemId
 * @param {import('../types/index.js').InventoryItem[]} [inventoryOverride]
 * @returns {Promise<boolean>} True if deleted, false if retained or skipped
 */
export async function deleteSupabaseStorageImageIfUnused(
  imageUrl,
  currentItemId,
  inventoryOverride = null
) {
  if (!imageUrl || isImageUsedByAnotherActiveItem(imageUrl, currentItemId, inventoryOverride)) {
    return false;
  }

  const bucket = getSupabaseStorageBucket();
  const { url, anonKey } = getSupabaseConfig();
  const objectPath = getSupabaseStorageObjectPath(imageUrl);
  if (!bucket || !objectPath) return false;

  const client = getSupabaseClient();
  if (client && client.storage && typeof client.storage.from === 'function') {
    const { error } = await client.storage.from(bucket).remove([objectPath]);
    if (error) {
      console.warn('Supabase storage remove error:', error);
      return false;
    }
    return true;
  }

  const encodedPath = objectPath
    .split('/')
    .filter(Boolean)
    .map(part => encodeURIComponent(part))
    .join('/');

  if (!url || !anonKey || !encodedPath) return false;

  const endpoint = `${url}/storage/v1/object/${encodeURIComponent(bucket)}/${encodedPath}`;
  const response = await fetch(endpoint, {
    method: 'DELETE',
    headers: {
      apikey: anonKey,
      Authorization: `Bearer ${getAuthAccessToken(anonKey)}`
    }
  });

  if (!response.ok && response.status !== 404) {
    const detail = await response.text().catch(() => '');
    throw new Error(detail || `Supabase Storage delete failed (${response.status})`);
  }

  return true;
}
